import { connect } from 'react-redux';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useField } from 'react-final-form';

import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { Section } from 'UIPackage/FormModule/components/Section';

import styles from './Judgment.module.scss';
import { IKnmFormRef } from '../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IRootState } from '../../CorePackage/MainModule/interfaces/IRootState';
import { IKnmErknmFormJudgment } from '../MainModule/interfaces/IKnmErknmFormJudgment';
import { DatePickerField } from '../../UIPackage/MainModule/components/DatePickerField';
import { Row } from '../../UIPackage/FormModule/components/Row';
import { FieldBlock } from '../../UIPackage/FormModule/components/FieldBlock';
import { TextInputField } from '../../UIPackage/MainModule/components/TextInputField';
import { SelectInputFieldAutoOptions } from '../../UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from '../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IKnmJudgmentCourtLevelSelectOption } from '../../CorePackage/CatalogsModule/interfaces/IKnmJudgmentCourtLevelSelectOption';
import { IKnmJudgmentCourtSelectOption } from '../../CorePackage/CatalogsModule/interfaces/IKnmJudgmentCourtSelectOption';
import {
  getDistrictSelectOptions,
  getJudgmentCourtLevelSelectOptions,
  getJudgmentCourtSelectOptions,
} from '../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { SelectInputField } from '../../UIPackage/MainModule/components/SelectInputField';
import { IKnmErknmForm } from '../MainModule/interfaces/IKnmErknmForm';
import { IKnmDistrictSelectOption } from '../../CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { Checkbox } from '../../UIPackage/MainModule/components/Checkbox';
import { JudgmentActionsEnum } from '../../CorePackage/CatalogsModule/enums/JudgmentActionsEnum';
import { ErknmJudgmentItemSubjectData } from './ErknmJudgmentItemSubjectData';
import {
  getJudgmentCanceledJudgmentItemBlockId,
  getJudgmentCanceledJudgmentsBlockId,
} from '../MainModule/constants/knmErknmFieldsElementsIds';
import { Button } from '../../UIPackage/MainModule/components/Button';
import { AddJudgmentModal } from './AddJudgmentModal';
import { IKnmErknmFormJudgmentCanceled } from '../MainModule/interfaces/IKnmErknmFormJudgmentCanceled';
import { ErknmJudgmentCancellationInfo } from './ErknmJudgmentCancellationInfo';
import { KnmJudgmentDocument } from '../../KnmPackage/KnmJudgmentModule/KnmJudgmentDocument';
import { JudgmentCreateCourtModal } from './JudgmentCreateCourtModal';
import { IJudgmentCourt } from '../../APIPackage/CatalogsModule/interfaces/IJudgmentCourt';
import { getCreatedJudgmentCourt, getKnmFormRef } from '../../KnmPackage/MainModule/selectors/knmSelectors';
import { KnmJudgmentActions } from '../../KnmPackage/KnmJudgmentModule/KnmJudgmentActions';
import { checkHasErrorInCollapseBlock } from '../../functions/checkHasErrorInCollapseBlock';
import { IKnmForm } from '../../KnmPackage/MainModule/interfaces/IKnmForm';
import { parseDate } from '../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from '../../CorePackage/MainModule/constants/dateFormats';

interface IErknmJudgmentItemProps {
  watchOnly: boolean;
  value: IKnmErknmFormJudgment;
  id: string;
  name: string;
  onRemove: () => void;
}

interface IErknmJudgmentItemStoreProps {
  formRef: IKnmFormRef | undefined;
  districtSelectOptions: IKnmDistrictSelectOption[] | undefined;
  judgmentCourtLevelSelectOptions: IKnmJudgmentCourtLevelSelectOption[] | undefined;
  judgmentCourtSelectOptions: IKnmJudgmentCourtSelectOption[] | undefined;
  createdCourt: IJudgmentCourt | undefined;
}

const ErknmJudgmentItemWithoutStore = ({
  formRef,
  watchOnly,
  value,
  name,
  id,
  onRemove,
  districtSelectOptions,
  judgmentCourtLevelSelectOptions,
  judgmentCourtSelectOptions,
  createdCourt,
}: IErknmJudgmentItemProps & IErknmJudgmentItemStoreProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const [knmCollapseOpenedList, setKnmCollapseOpenedList] = useState<{ [key: string]: boolean }>({});
  const remove = formRef?.current.mutators.remove;
  const { isNew: isNewJudgment } = value;
  const [filteredCourtSelectOptions, setFilteredCourtSelectOptions] = useState(
    judgmentCourtSelectOptions?.filter(x => x.courtCode === 'NEW'),
  );
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isCreateCourtModalVisible, setIsCreateCourtModalVisible] = useState<boolean>(false);

  const startDate = useField('startDate' as keyof IKnmForm, { subscription: { value: true } }).input.value;

  const minJudgmentDate = useMemo(() => {
    if (!startDate) {
      return undefined;
    }

    return parseDate({ date: startDate, parseFormat: DATE_FORMAT_LONG_FNS });
  }, [startDate]);

  const handleDistrictChange = useCallback(
    (v: IKnmDistrictSelectOption) => {
      if (v) {
        setFilteredCourtSelectOptions(
          judgmentCourtSelectOptions?.filter(x => `0${x.rfRegionCode}` === v.code || x.courtCode === 'NEW'),
        );
      } else {
        setFilteredCourtSelectOptions(judgmentCourtSelectOptions?.filter(x => x.courtCode === 'NEW'));
      }
      formRef?.current.change(`${name}.court` as keyof IKnmErknmForm);
      formRef?.current.change(`${name}.courtLevelCode` as keyof IKnmErknmForm);
    },
    [formRef, judgmentCourtSelectOptions],
  );

  const handleCourtChange = useCallback(
    (v: IKnmJudgmentCourtSelectOption) => {
      if (v) {
        if (v.code === 'NEW') {
          formRef?.current.change(`${name}.court` as keyof IKnmErknmForm); // очистить временное техническое значение NEW, используемое как флаг, транспортируемый через поле формы
          openCreateCourtModal();

          return;
        }

        formRef?.current.change(
          `${name}.courtLevelCode` as keyof IKnmErknmForm,
          judgmentCourtLevelSelectOptions?.find(x => x.value === v.courtTypeCode),
        );
        formRef?.current.change(
          `${name}.rfRegionCode` as keyof IKnmErknmForm,
          // @ts-ignore
          districtSelectOptions?.find(x => x.code === `0${v.rfRegionCode}`),
        );
      } else {
        formRef?.current.change(`${name}.courtLevelCode` as keyof IKnmErknmForm);
      }
    },
    [formRef, judgmentCourtLevelSelectOptions],
  );

  const handleCourtLevelChange = useCallback(
    (v: IKnmJudgmentCourtLevelSelectOption) => {
      if (v) {
        setFilteredCourtSelectOptions(
          filteredCourtSelectOptions?.filter(x => x.courtTypeCode === v.code || x.courtCode === 'NEW'),
        );
      } else {
        // @ts-ignore
        const selectedRegion: IKnmDistrictSelectOption = formRef?.current.getFieldState(
          `${name}.rfRegionCode` as keyof IKnmErknmForm,
        )?.value;

        if (selectedRegion) {
          setFilteredCourtSelectOptions(
            judgmentCourtSelectOptions?.filter(
              x => `0${x.rfRegionCode}` === selectedRegion.code || x.courtCode === 'NEW',
            ),
          );
        } else {
          setFilteredCourtSelectOptions(judgmentCourtSelectOptions?.filter(x => x.courtCode === 'NEW'));
        }
      }
    },
    [formRef, filteredCourtSelectOptions],
  );

  const handleChangeJudgmentAction = useCallback(() => {
    if (isNewJudgment) {
      formRef?.current.change(`${name}.judgmentSubjectData` as keyof IKnmErknmForm);
      formRef?.current.change(`${name}.createNewJudgment` as keyof IKnmErknmForm, false);
      formRef?.current.change(`${name}.canceledJudgments` as keyof IKnmErknmForm);
    }
  }, [formRef, isNewJudgment]);

  const openModal = () => {
    setIsModalVisible(true);
  };

  const saveModal = () => {
    setIsModalVisible(false);
  };

  const cancelModal = useCallback(() => {
    formRef?.current.change(`${name}.canceledJudgments` as keyof IKnmErknmForm, value.canceledJudgments);
    setIsModalVisible(false);
  }, [formRef, value, name]);

  const handleCreateNewJudgmentCheckboxChange = useCallback(
    (v: ChangeEvent<HTMLInputElement>) => {
      formRef?.current.change(`${name}.createNewJudgment` as keyof IKnmErknmForm, v.target.checked);
      if (!v.target.checked) {
        formRef?.current.change(`${name}.judgmentSubjectData` as keyof IKnmErknmForm);
      }
    },
    [formRef, name],
  );

  const makeRemoveCanceledJudgmentHandler = (index: number) => () =>
    !watchOnly && remove && remove(`${name}.canceledJudgments`, index);

  const openCreateCourtModal = () => {
    setIsCreateCourtModalVisible(true);
  };

  const closeCreateCourtModal = () => {
    setIsCreateCourtModalVisible(false);
  };

  useEffect(() => {
    if (isNewJudgment && createdCourt && judgmentCourtSelectOptions) {
      formRef?.current.change(
        `${name}.court` as keyof IKnmErknmForm,
        judgmentCourtSelectOptions.find(x => x.id === createdCourt.id),
      );
      if (createdCourt.rfRegion) {
        formRef?.current.change(
          `${name}.rfRegionCode` as keyof IKnmErknmForm,
          // @ts-ignore
          districtSelectOptions?.find(x => x.code === `0${createdCourt.rfRegion}`),
        );
      }
      if (createdCourt.courtType) {
        formRef?.current.change(
          `${name}.courtLevelCode` as keyof IKnmErknmForm,
          judgmentCourtLevelSelectOptions?.find(x => x.value === createdCourt.courtType),
        );
      }
      setFilteredCourtSelectOptions(
        judgmentCourtSelectOptions?.filter(x => x.rfRegionCode === createdCourt.rfRegion || x.courtCode === 'NEW'),
      );
    }
  }, [createdCourt, judgmentCourtSelectOptions]);

  const hasError = checkHasErrorInCollapseBlock(formRef, name);

  const hasJudgmentFile = Boolean(value.attachment?.length);

  const isAttachmentEditable =
    isNewJudgment || !hasJudgmentFile || Boolean(value?.attachment?.some(item => item.isNew));

  return (
    <>
      <AddJudgmentModal
        formRef={formRef}
        visible={isModalVisible}
        modalType={'judgments'}
        judgmentItemName={name}
        onSave={saveModal}
        onCancel={cancelModal}
        judgements={formRef?.current
          .getFieldState('judgments')
          ?.value?.filter(x => !x.isNew && x.judgmentAction !== JudgmentActionsEnum.ANNULMENT_JUDGMENT)}
      />
      <JudgmentCreateCourtModal
        visible={isCreateCourtModalVisible}
        onClose={closeCreateCourtModal}
        watchOnly={watchOnly}
        judgmentCourtLevelSelectOptions={judgmentCourtLevelSelectOptions}
        districtSelectOptions={districtSelectOptions}
        selectedRegion={value.rfRegionCode}
      />
      <KnmCollapse
        theme="fields"
        title={isNewJudgment ? 'Решение' : `Решение №${value.numberJudgment}`}
        id={id}
        isNew={isNewJudgment}
        isNewTitle={'Новое'}
        opened={isNewJudgment}
        onClose={isNewJudgment ? onRemove : undefined}
        error={isKnmCollapseOpened && hasError}
        changeOpenedCallback={setIsKnmCollapseOpened}
      >
        <>
          <Row>
            <FieldBlock id={`${id}.dateJudgment`} label={<>Дата судебного решения</>} size="half">
              <DatePickerField
                name={`${name}.dateJudgment`}
                placeholder="Выберите дату судебного решения"
                disabled={watchOnly || !isNewJudgment}
                minDate={minJudgmentDate}
              />
            </FieldBlock>
            <FieldBlock id={`${id}.numberJudgment`} label={<>Номер судебного решения</>} size="half">
              <TextInputField
                name={`${name}.numberJudgment`}
                placeholder="Введите номер судебного решения"
                disabled={watchOnly || !isNewJudgment}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock id={`${id}.kindJudgment`} label={<>Вид решения</>}>
              <SelectInputFieldAutoOptions
                name={`${name}.kindJudgment`}
                isClearable={false}
                size="large"
                optionName={CommonCatalogsNamesEnum.JUDGMENT_KINDS}
                disabled={watchOnly || !isNewJudgment}
              />
            </FieldBlock>
          </Row>

          <Row id={`${id}.attachment`}>
            <KnmJudgmentDocument
              watchOnly={watchOnly || !isAttachmentEditable}
              disableToAdd={hasJudgmentFile}
              name={`${name}.attachment`}
              formRef={formRef}
            />
          </Row>

          <Row>
            <FieldBlock id={`${id}.rfRegionCode`} label={<>Субъект Российской Федерации</>}>
              <SelectInputFieldAutoOptions
                name={`${name}.rfRegionCode`}
                placeholder="Выберите субъект Российской Федерации"
                isClearable={true}
                size="medium"
                optionName={CommonCatalogsNamesEnum.DISTRICTS}
                disabled={watchOnly || !isNewJudgment}
                onChange={handleDistrictChange}
                isSearchable
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock id={`${id}.court`} label={<>Наименование суда</>}>
              <SelectInputField
                name={`${name}.court`}
                placeholder="Выберите наименование суда"
                options={filteredCourtSelectOptions}
                disabled={watchOnly || !value.rfRegionCode || !isNewJudgment}
                size="medium"
                onChange={handleCourtChange}
                isSearchable
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock id={`${id}.courtLevelCode`} label={<>Уровень суда</>}>
              <SelectInputField
                name={`${name}.courtLevelCode`}
                placeholder="Выберите уровень суда"
                options={judgmentCourtLevelSelectOptions}
                disabled={watchOnly || value.court?.code !== 'NEW' || !isNewJudgment}
                size="medium"
                onChange={handleCourtLevelChange}
              />
            </FieldBlock>
          </Row>

          <KnmJudgmentActions
            handleChangeJudgmentAction={handleChangeJudgmentAction}
            id={id}
            isNewJudgment={isNewJudgment}
            name={name}
            value={value}
            watchOnly={watchOnly}
          />

          {(value.judgmentAction === JudgmentActionsEnum.ADD_JUDGMENT ||
            value.judgmentAction === JudgmentActionsEnum.ANNULMENT_AKT) && (
            <ErknmJudgmentItemSubjectData
              watchOnly={watchOnly || !isNewJudgment}
              judgmentItemValue={value}
              judgmentItemId={id}
              judgmentItemName={`${name}`}
              judgmentItemSubjectDataId={`${id}.judgmentSubjectData`}
            />
          )}
          {value.judgmentAction === JudgmentActionsEnum.ANNULMENT_JUDGMENT && (
            <Section
              title="Сведения о судебных решениях в рамках КНМ, которые отменены"
              type="list"
              id={getJudgmentCanceledJudgmentsBlockId(id)}
              titleId={getJudgmentCanceledJudgmentsBlockId(id)}
              customButton={
                <Button
                  className="shared-btn-add"
                  text="Добавить"
                  type="button"
                  variant="link"
                  color="primary"
                  disabled={watchOnly || !isNewJudgment}
                  onClick={openModal}
                />
              }
            >
              <FieldArray name={`${name}.canceledJudgments`}>
                {({ fields }: FieldArrayRenderProps<IKnmErknmFormJudgmentCanceled, HTMLElement>) => {
                  if (!fields.length) {
                    return null;
                  }

                  return fields.map((canceledJudgmentName, canceledJudgmentIndex) => {
                    const canceledJudgmentValue: IKnmErknmFormJudgmentCanceled = fields.value?.[canceledJudgmentIndex];
                    const canceledJudgmentId = getJudgmentCanceledJudgmentItemBlockId(id, canceledJudgmentValue.listId);

                    const hasErrorList = checkHasErrorInCollapseBlock(formRef, canceledJudgmentName);

                    return (
                      <KnmCollapse
                        theme="fields"
                        title={`Судебное решение №${canceledJudgmentValue.numberJudgment}`}
                        key={canceledJudgmentId}
                        id={canceledJudgmentId}
                        isNew={false}
                        opened={true}
                        onClose={isNewJudgment ? makeRemoveCanceledJudgmentHandler(canceledJudgmentIndex) : undefined}
                        bodyClassName={styles.CancellationCollapseField}
                        changeOpenedCallback={e =>
                          setKnmCollapseOpenedList(state => ({
                            ...state,
                            [canceledJudgmentName]: e,
                          }))
                        }
                        error={hasErrorList && knmCollapseOpenedList[canceledJudgmentName]}
                      >
                        <ErknmJudgmentCancellationInfo
                          judgmentCancellationItemId={canceledJudgmentId}
                          judgmentCancellationItemName={canceledJudgmentName}
                          watchOnly={watchOnly || !isNewJudgment}
                          judgmentSubjectData={canceledJudgmentValue.judgmentSubjectData}
                        />
                      </KnmCollapse>
                    );
                  });
                }}
              </FieldArray>
              {value.canceledJudgments && (
                <>
                  <Row>
                    <FieldBlock id={`${id}.createNewJudgment`} size="content">
                      <Checkbox
                        name={`${name}.createNewJudgment`}
                        checked={value.createNewJudgment}
                        label="Судебным актом принято новое решение в отношении оспариваемых документов КНМ"
                        type="checkbox"
                        theme="system"
                        onChange={handleCreateNewJudgmentCheckboxChange}
                        disabled={watchOnly || !isNewJudgment}
                      />
                    </FieldBlock>
                  </Row>

                  {value.createNewJudgment && (
                    <ErknmJudgmentItemSubjectData
                      watchOnly={watchOnly || !isNewJudgment}
                      judgmentItemValue={value}
                      judgmentItemId={id}
                      judgmentItemName={`${name}`}
                      judgmentItemSubjectDataId={`${id}.judgmentSubjectData`}
                    />
                  )}
                </>
              )}
            </Section>
          )}
        </>
      </KnmCollapse>
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmJudgmentItemStoreProps => ({
  formRef: getKnmFormRef(state),
  districtSelectOptions: getDistrictSelectOptions(state),
  judgmentCourtLevelSelectOptions: getJudgmentCourtLevelSelectOptions(state),
  judgmentCourtSelectOptions: getJudgmentCourtSelectOptions(state),
  createdCourt: getCreatedJudgmentCourt(state),
});

export const ErknmJudgmentItem = connect(mapStateToProps)(ErknmJudgmentItemWithoutStore);
